import getConfig from "@/utils/getConfig";
import {DevourPlayApi, GameHealthResponse} from "@devour/client";
import {useQuery} from "@tanstack/react-query";

/**
 * Public game-health status for a gameId — backed by the server cron's
 * GameTrackingHealthSnapshot. Used by the status chip + at-risk
 * competition banner to surface RED/YELLOW/GREEN tiers and the top
 * failing GEP features when scoring is degraded.
 *
 * 60-second stale time matches the server-side Redis cache (the endpoint
 * itself caches at the same TTL), so client + server windows are aligned.
 * Refetch on interval is intentionally OFF — the chip polls when the user
 * navigates / the app foregrounds, which is the right granularity for a
 * status indicator that updates on a 15-minute compute cycle anyway.
 */
async function fetchGameHealth(gameId: string): Promise<GameHealthResponse> {
    return await new DevourPlayApi(getConfig()).getGameHealthGameId({
        gameId,
    });
}

export default function useGameHealth(gameId: string | undefined) {
    return useQuery({
        queryKey: [
            "game-health",
            gameId,
        ],
        queryFn: () => fetchGameHealth(gameId!),
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: typeof gameId === "string" && gameId.length > 0,
    });
}
